import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import { appPropTypes } from '@techchange/components';

import {
	RESOURCE_TYPES,
	RESOURCE_ASSOCIATED_OBJECT_TYPES,
} from '../../constants';

import ContentGridAdmin from './ContentGridAdmin';
import ContentGridStudent from './ContentGridStudent';
import types from './utils/types';

import './ResourceContentGrid.scss';

const propTypes = {

	/**
	 * The parent content resource.
	 */
	resource: PropTypes.shape({

		/**
		 * The unique ID of the resource.
		 */
		id: PropTypes.string,

		/**
		 * The resource url.
		 */
		url: PropTypes.string.isRequired,

		/**
		 * The resource path.
		 */
		path: PropTypes.string.isRequired,

		/**
		 * The resource type.
		 */
		type: PropTypes.oneOf([
			RESOURCE_TYPES.PAGE,
			RESOURCE_TYPES.MODULE,
			RESOURCE_TYPES.SUBMODULE,
			RESOURCE_TYPES.SUBMODULE_CONTENT,
		]).isRequired,

		/**
		 * The resource title.
		 */
		title: PropTypes.string,

		/**
		 * The resource slug.
		 */
		slug: PropTypes.string.isRequired,

		/**
		 * The type of object that is associated with the parent content resource.
		 *
		 * i.e. A discussion thread, event, or product.
		 *
		 * NOTE: This field is only required for the "submodule-content" root resources,
		 *       as "page" root resources are standalone objects for now.
		 */
		associated_object_type: PropTypes.oneOf([
			RESOURCE_ASSOCIATED_OBJECT_TYPES.DISCUSSION_THREAD,
			RESOURCE_ASSOCIATED_OBJECT_TYPES.EVENT,
			RESOURCE_ASSOCIATED_OBJECT_TYPES.EVENT_STAGE,
			RESOURCE_ASSOCIATED_OBJECT_TYPES.PRODUCT,
		]),

		/* The ID of the object that is associated with the parent content resource.
		 *
		 * i.e. A discussion thread, event, or product.
		 *
		 * NOTE: This field is only required for the "submodule-content" root resources,
		 *       as "page" root resources are standalone objects for now.
		 */
		associated_object_id: PropTypes.string,

		/**
		 * The resource scope.
		 */
		scope: PropTypes.string.isRequired,

		/**
		 * The resource scope ID.
		 */
		scope_id: PropTypes.string.isRequired,

		/**
		 * The resource organization ID.
		 */
		organization_id: PropTypes.string.isRequired,

		/**
		 * The timestamp of resource creation (if any).
		 */
		created_at: PropTypes.string,

		/**
		 * The children resources (if any).
		 */
		children: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The child resource ID.
			 */
			id: PropTypes.string,

		})),

	}).isRequired,

	/**
	 * True, if the user has admin privileges to create/edit/delete resources.
	 */
	isAdmin: PropTypes.bool.isRequired,

	/**
	 * The current user's ID.
	 */
	userId: PropTypes.string,

	/**
	 * The current user's timezone.
	 */
	userTimezone: PropTypes.string.isRequired,

	/**
	 * Admins Only: API integrations data for the current user.
	 */
	apiIntegrations: appPropTypes.apiIntegrations,

	/**
	 * The settings for filestack to handle image uploads and rendering.
	 */
	filestack: appPropTypes.filestack.isRequired,

	/**
	 * The users related to the scope of this content,
	 * (i.e. all enrolled users in a session).
	 *
	 * NOTE: Map from user ID -> user data.
	 */
	users: appPropTypes.allUsers,

	/**
	 * The TechChange socket instance to handle real-time events.
	 */
	socket: appPropTypes.socket,

	/**
	 * The location.pathname, provided by react-router-dom.
	 */
	pathname: PropTypes.string.isRequired,

	/**
	 * True, if the resource supports completion tracking (defaults to false).
	 */
	isUsingCompletionTracking: PropTypes.bool.isRequired,

	/**
	 * A map of object ID -> rule/condition/subcondition/trackers for each rule in the scope.
	 */
	rulesByObjectID: PropTypes.objectOf(PropTypes.shape({

		/**
		 * The rule ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The rule condition.
		 */
		condition: PropTypes.shape({

			/**
			 * The condition ID.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The rule subconditions.
			 */
			subconditions: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The subcondition ID.
				 */
				id: PropTypes.string.isRequired,

				/**
				 * The subcondition completion tracker (if any) for the current user.
				 */
				tracker: PropTypes.shape({

					/**
					 * The completion tracker status.
					 */
					status: PropTypes.string.isRequired,

				}),

			})).isRequired,

			/**
			 * The condition completion tracker (if any) for the current user.
			 */
			tracker: PropTypes.shape({

				/**
				 * The completion tracker status.
				 */
				status: PropTypes.string.isRequired,

			}),

		}).isRequired,

		/**
		 * The rule completion tracker (if any) for the current user.
		 */
		tracker: PropTypes.shape({

			/**
			 * The completion tracker status.
			 */
			status: PropTypes.string.isRequired,

		}),

	})),

	/**
	 * A map of ID -> progress percentage of the user for each rule in the scope.
	 */
	progressByRuleID: PropTypes.objectOf(PropTypes.number),

	/**
	 * The list of gravity forms (DEPRECATED) for the scope.
	 */
	gravityForms: PropTypes.arrayOf(PropTypes.shape({

		title: PropTypes.string.isRequired,

		tc_uuid: PropTypes.string.isRequired,

	})),

	/**
	 * Configuration settings for the resource types to use in Content Builder.
	 */
	config: types.config.isRequired,

	/**
	 * An optional label for the button to toggle open the dropdown menu
	 * to add a new piece of content to the grid.
	 */
	dropdownMenuPlaceholderLabel: PropTypes.string,

	/**
	 * Callback function to handle using an API integration.
	 */
	onUseApiIntegration: PropTypes.func,

	/**
	 * Callback function to handle changes to the content resource.
	 */
	onChangeResource: PropTypes.func.isRequired,

	/**
	 * Callback function to handle clicking a link related to the content resource.
	 */
	onClickResourceLink: PropTypes.func,

	/**
	 * Callback function to handle clicking on a user.
	 */
	onClickUser: PropTypes.func,

	/**
	 * Callback function (returning a promise) to handle saving the rule for a resource.
	 */
	onSaveRule: PropTypes.func,

};

const defaultProps = {
	isAdmin: false,
	isUsingCompletionTracking: false,
	config: {
		[RESOURCE_TYPES.MIRO]: true,
		[RESOURCE_TYPES.SLIDE]: true,
		[RESOURCE_TYPES.IMAGE]: true,
		[RESOURCE_TYPES.VIDEO]: true,
		[RESOURCE_TYPES.EMBED]: true,
		[RESOURCE_TYPES.FORM]: true,
		[RESOURCE_TYPES.POLL]: true,
		[RESOURCE_TYPES.QUIZ]: true,
	},
};

/**
 * Shared component to render the resource content grid.
 *
 * @param {Object} props The props.
 *
 * @returns {Object} The rendered component.
 */
const ResourceContentGrid = (props) => {

	const {
		resource,
		isAdmin,
		...restProps
	} = props;

	// Keep track of any changes to a content resource while editing that
	// will cause its position in the content grid to re-render
	const [gridChanges, setGridChanges] = useState({});

	/**
	 * Handles local changes to the content grid (i.e. resource width) for an edited piece of content.
	 *
	 * NOTE: When this happens, we update the display of the content locally so that
	 * the changes can be visible in real-time without needing to persist the changes.
	 *
	 * i.e. for changes to width/weight that would cause the grid and content editor to re-render
	 *
	 * @param {string} id            The resource id.
	 * @param {object} data          The data that changed for the resource.
	 */
	const handleWidthChange = useCallback((id, data) => {

		setGridChanges((prev) => {

			// Create a copy of the local changes to the content grid
			const result = Object.assign({}, prev);

			// Keep track of the data that changed for the content, i.e width, weight, title, etc.
			if (data) {

				result[id] = Object.assign({}, result[id], data);

			} else {

				Reflect.deleteProperty(result, id);

			}

			return result;

		});

	}, []);

	/**
	 * Updates the content that have had local changes while being edited.
	 *
	 * @param   {array} children  The children for the content resource.
	 *
	 * @returns {array} result    The up-to-date children for the content resource.
	 */
	const mockGridChanges = (children) => {

		// Check if there are any local changes to edited content resources
		if (Object.keys(gridChanges).length === 0) {
			return children;
		}

		// Check if any edited resources have a local change which would affect the grid layout
		const result = children.map((child) => {

			const updatedChild = Object.assign({}, child);

			// Check if the changes apply to this child
			if (gridChanges[updatedChild.id]) {

				const data = gridChanges[updatedChild.id];

				// Update the resource with the data that changed
				updatedChild.title = data.title;
				updatedChild.content = data.content;
				updatedChild.data = data.data;
				updatedChild.width = data.width;
				updatedChild.language = data.language;
				updatedChild.resource_contents = data.resource_contents;
				updatedChild.deleted_resource_contents = data.deleted_resource_contents;

			}

			return updatedChild;

		});

		return result;

	};

	/**
	 * Builds out the grid of content row by row based on the respective content widths.
	 *
	 * @param   {array} children  The children for the content resource.
	 *
	 * @returns {array} result    The content grid (row by row).
	 */
	const buildContentGrid = (children) => {

		const result = [];

		let currentRow = []; // The current row

		let currentRowWidth = 0; // The current row width

		// Loop through each of the children for the content resource
		children.forEach((child) => {

			// Check if this piece of content can't fit in the current row
			if (currentRowWidth + child.width > 12) {

				// Add the current row to the result
				result.push(currentRow);

				currentRowWidth = 0; // Reset
				currentRow = []; // Reset

			}

			// Add this content to the current row
			currentRow.push(child);
			currentRowWidth += child.width;

		});

		// Check if there is any content leftover
		if (currentRow.length > 0) {
			result.push(currentRow);
		}

		return result;

	};

	// Get the list of children for the content resource
	const children = resource.children || [];

	// We need to mock any real-time changes to the content grid while editing
	const mockedChildren = mockGridChanges(children);

	// Build the content grid row-by-row for the content resource
	const gridRows = buildContentGrid(mockedChildren);

	if (isAdmin) {

		return (
			<div className="submodule-content">
				<ContentGridAdmin
					resource={resource}
					gridRows={gridRows}
					gridChanges={gridChanges}
					onWidthChange={handleWidthChange}
					{...restProps} />
			</div>
		);
	}

	return (
		<div className="submodule-content">
			<ContentGridStudent
				resource={resource}
				gridRows={gridRows}
				{...restProps} />
		</div>
	);

};

ResourceContentGrid.defaultProps = defaultProps;
ResourceContentGrid.propTypes = propTypes;

export default ResourceContentGrid;
